// lib
import { useState, useRef, useCallback, useEffect } from 'react'

import fetch from 'isomorphic-fetch'

import railsPostFetch, { getCSRFToken } from '@aytm/lib/common/rails-post-fetch'

import ProgressivePromise from '@aytm/lib/AsyncJobManager/ProgressivePromise'

import FileUpload from '@aytm/app/stats/lib/io/file-upload'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// Adapter for the stat microservice upload contract at PageGlobals.STAT_SERVER_URL:
//   - cross-origin (credentials: 'omit', mode: 'cors', cache: 'no-cache')
//   - create-asset returns flat { url, fileId } (not the Rails upload_urls envelope)
//   - finish-upload returns { file: { md5 } } whose md5 is needed to build the status URL
//   - status strings are 'OK' / 'RUNNING' (not Rails' 'ready' / 'processing')
//   - resolves with { asset: { id, md5 }, result: uploadStatus } for the caller
const statServerAdapter = {
  createAsset: ({ file, params }) => {
    const body = { ...params, filename: file.name }
    return railsPostFetch(`${PageGlobals.STAT_SERVER_URL}/upload`, {
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-cache',
      body: JSON.stringify(body)
    })
      .then(response => (response.ok ? response.json() : {}))
      .then(data => {
        if (!data || !data.url) {
          return Promise.reject(new Error('FileUpload - invalid response data'))
        }
        return {
          uploadUrl: data.url,
          finishUrl: `${PageGlobals.STAT_SERVER_URL}/survey-variables/${data.fileId}`,
          asset: { id: data.fileId }
        }
      })
  },

  finishUpload: ({ file, params, finishUrl, asset }) =>
    fetch(finishUrl, {
      method: 'POST',
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken()
      },
      Accept: 'application/json',
      body: JSON.stringify({ ...params, filename: file.name })
    })
      .then(response => (response.ok ? response.json() : {}))
      .then(data => {
        if (!data.file) {
          return Promise.reject(new Error('FileUpload - unable to finish upload'))
        }
        const next = { id: asset.id, md5: data.file.md5 }
        return {
          asset: next,
          statusUrl: `${PageGlobals.STAT_SERVER_URL}/survey-variables/${next.id}/${next.md5}`
        }
      }),

  checkStatus: ({ statusUrl, params }) =>
    fetch(statusUrl, {
      method: 'POST',
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken()
      },
      Accept: 'application/json',
      body: JSON.stringify(params)
    }).then(response => (response.ok ? response.json() : {})),

  terminalStatus: 'OK',
  processingStatus: 'RUNNING',
  buildResult: (response, asset) => ({ asset, result: response })
}

// A delete job that never reports finished would keep its row spinning for as long as the page is
// open, so the poll gives up after this long and the panel re-reads the variables instead.
export const DELETE_WAIT_MS = 10 * 60 * 1000
export const DELETE_STILL_RUNNING_WARNING =
  'The variable is still being deleted. It will disappear from the list once the deletion finishes.'

const useVariables = ({ dispatch, onFetch, onFetchApiKey }) => {
  const [errorList, setErrorList] = useState([])
  const [processingList, setProcessingList] = useState([])

  const uploadQueue = useRef([])
  const uploadQueueCallbacks = useRef({})

  const abortControllerRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    abortControllerRef.current = new AbortController()
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const checkQueueCallbacks = () => {
    if (!uploadQueue.current.length) return

    if (uploadQueue.current.length == Object.keys(uploadQueueCallbacks.current).length) {
      let i = 0
      while (!uploadQueueCallbacks.current[uploadQueue.current[i]]) i++

      uploadQueueCallbacks.current[uploadQueue.current[i]]()
      delete uploadQueueCallbacks.current[uploadQueue.current[i]]
    }
  }

  const checkApiKey = async (variable, callback) => {
    if (!variable || 'function' != typeof callback) return false

    const apiKey = await onFetchApiKey()

    uploadQueue.current.push(variable)
    uploadQueueCallbacks.current[variable] = callback

    checkQueueCallbacks()

    return apiKey
  }

  const checkUploadQueue = variable => {
    const index = uploadQueue.current.indexOf(variable)
    uploadQueue.current.splice(index, 1)

    if (0 < uploadQueue.current.length) {
      checkQueueCallbacks()
    } else {
      onFetch(null, () => setProcessingList([]))
    }
  }
  const checkUploadQueueError = variable => {
    const index = uploadQueue.current.indexOf(variable)
    uploadQueue.current.splice(index, 1)

    setErrorList([...errorList, variable].filter((v, i, a) => a.indexOf(v) == i))
    setProcessingList(processingList.filter(v => v != variable))

    checkQueueCallbacks()
  }

  const onVariableUpload = file => {
    dispatch({
      type: 'UPDATE_STATE',
      key: 'variables',
      state: {
        uploading: true
      }
    })

    return checkApiKey('upload', async () => await onFetchApiKey()).then(accessKey => {
      const abortSignal = abortControllerRef.current?.signal

      FileUpload({
        file,
        contentType: 'application/octet-stream',
        params: { key: accessKey },
        abortSignal,
        adapter: statServerAdapter
      })
        .then(
          result => {
            checkUploadQueue('upload')
            return result.result
          },
          null,
          (progress, totalProgress) => {
            const percents = (100 * progress) / totalProgress

            dispatch({
              type: 'UPDATE_STATE',
              key: 'variables',
              state: {
                uploading: 95 >= percents,
                processing: 95 < percents
              }
            })
          }
        )
        .then(result => {
          dispatch({
            type: 'UPDATE_STATE',
            key: 'variables',
            state: {
              messages: (result && result.messages) || [],
              errors: [],
              warnings: (result && result.warnings) || [],
              uploading: false,
              processing: false
            }
          })
        })
        .catch(error => {
          dispatch({
            type: 'UPDATE_STATE',
            key: 'variables',
            state: {
              messages: [],
              errors: error.errors && 0 < error.errors.length ? error.errors : ["We couldn't process your file"],
              warnings: error.warnings || [],
              uploading: false,
              processing: false
            }
          })

          checkUploadQueueError('upload')
        })
    })
  }

  const onVariableRemove = variable => {
    if (0 <= uploadQueue.current.indexOf(variable)) return false

    setErrorList(errorList.filter(v => v != variable))
    setProcessingList([...processingList, variable].filter((v, i, a) => a.indexOf(v) == i))

    return checkApiKey(variable, async () => {
      const apiKey = await onFetchApiKey()
      const params = {
        key: apiKey
      }
      const abortSignal = abortControllerRef.current?.signal

      return new ProgressivePromise((resolve, reject) => {
        const deleteParams = Object.assign({}, params, {
          variable
        })

        railsPostFetch(`${PageGlobals.STAT_SERVER_URL}/survey-variables`, {
          method: 'DELETE',
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(deleteParams)
        })
          .then(response => (response.ok ? response.json() : {}))
          .then(data => {
            if (abortSignal?.aborted) {
              return Promise.reject(new Error('Aborted'))
            }
            if (data.status && 'OK' == data.status) {
              return Promise.resolve()
            } else if (data.token) {
              const statusParams = Object.assign({}, params, {
                token: data.token
              })
              return new Promise((resolve, reject) => {
                const startedAt = Date.now()
                const checkStatus = () => {
                  if (abortSignal?.aborted) {
                    reject(new Error('Aborted'))
                    return
                  }
                  if (DELETE_WAIT_MS <= Date.now() - startedAt) {
                    reject({ stillRunning: true })
                    return
                  }
                  railsPostFetch(`${PageGlobals.STAT_SERVER_URL}/survey-variables`, {
                    method: 'DELETE',
                    credentials: 'omit',
                    mode: 'cors',
                    cache: 'no-cache',
                    body: JSON.stringify(statusParams)
                  })
                    .then(response => (response.ok ? response.json() : {}))
                    .then(deleteStatus => {
                      if (abortSignal?.aborted) {
                        reject(new Error('Aborted'))
                        return
                      }
                      switch (deleteStatus.status) {
                        case 'OK': {
                          resolve()
                          break
                        }
                        case 'RUNNING': {
                          setTimeout(checkStatus, 1000)
                          break
                        }
                        default: {
                          reject(deleteStatus)
                          break
                        }
                      }
                    })
                    .catch(_error => {
                      if (!abortSignal?.aborted) {
                        setTimeout(checkStatus, 1000)
                      }
                    })
                }
                setTimeout(checkStatus, 500)
              })
            } else {
              return Promise.reject(new Error('VariablesRemover - unable to finish delete'))
            }
          })
          .then(resolve, reject)
          .catch(error => reject(error))
      })
        .then(() => checkUploadQueue(variable))
        .catch(error => {
          // the job has not failed, it has not finished yet: stop the spinner and re-read the list
          // the way a finished delete does, so the row goes once the deletion lands
          if (error?.stillRunning) {
            dispatch({
              type: 'UPDATE_STATE',
              key: 'variables',
              state: { messages: [], errors: [], warnings: [DELETE_STILL_RUNNING_WARNING] }
            })
            checkUploadQueue(variable)
            return
          }

          dispatch({
            type: 'UPDATE_STATE',
            key: 'variables',
            state: {
              messages: [],
              errors: error.errors && 0 < error.errors.length ? error.errors : ["We couldn't process your file"],
              warnings: error.warnings || [],
              uploading: false,
              processing: false
            }
          })

          checkUploadQueueError(variable)
        })
    })
  }

  const getVariableError = useCallback(
    filter =>
      Object.hasOwn(filter, 'derived')
        ? errorList.some(key => 0 <= filter.derived.indexOf(key))
        : errorList.includes(filter.key),
    [errorList]
  )
  const getVariableProcessing = useCallback(
    filter =>
      Object.hasOwn(filter, 'derived')
        ? processingList.some(key => 0 <= filter.derived.indexOf(key))
        : processingList.includes(filter.key),
    [processingList]
  )

  return { onVariableUpload, onVariableRemove, getVariableError, getVariableProcessing }
}

export default useVariables
